/**
 * OFFilters - Componente de filtros para Ordens de Fabrico
 * Extraído de GestaoOFs.js para melhor modularidade
 */
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Search, X, Clock, Trash2 } from 'lucide-react';

const STATUS_CONFIG = {
  rascunho: { label: 'Rascunho' },
  pendente: { label: 'Ready to Start' },
  aguardar_entrada: { label: 'Aguardar Entrada' },
  em_curso: { label: 'Em Curso' },
  pausado: { label: 'Pausado' },
  terminado: { label: 'Terminado' },
  cancelado: { label: 'Cancelado' },
};

export default function OFFilters({
  searchTerm,
  setSearchTerm,
  filterCliente,
  setFilterCliente,
  filterStatus,
  setFilterStatus,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  clientesUnicos,
}) {
  const [showHistory, setShowHistory] = useState(false);
  const [searchHistory, setSearchHistory] = useState(() => {
    const saved = localStorage.getItem('ofs_search_history');
    return saved ? JSON.parse(saved) : [];
  });
  const searchRef = useRef(null);
  const historyRef = useRef(null);

  // Fechar histórico ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (historyRef.current && !historyRef.current.contains(event.target) &&
          searchRef.current && !searchRef.current.contains(event.target)) {
        setShowHistory(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Adicionar ao histórico quando pesquisa
  const addToHistory = (term) => {
    if (!term.trim()) return;
    const newHistory = [term, ...searchHistory.filter(h => h.toLowerCase() !== term.toLowerCase())].slice(0, 7);
    setSearchHistory(newHistory);
    localStorage.setItem('ofs_search_history', JSON.stringify(newHistory));
  };

  // Remover item do histórico
  const removeFromHistory = (term, e) => {
    e.stopPropagation();
    const newHistory = searchHistory.filter(h => h !== term);
    setSearchHistory(newHistory);
    localStorage.setItem('ofs_search_history', JSON.stringify(newHistory));
  };

  // Limpar histórico completo
  const clearHistory = (e) => {
    e.stopPropagation();
    setSearchHistory([]);
    localStorage.removeItem('ofs_search_history');
  };

  // Usar termo do histórico
  const selectHistoryTerm = (term) => {
    setSearchTerm(term);
    setShowHistory(false);
    addToHistory(term);
  };

  // Limpar pesquisa
  const handleClearSearch = () => {
    setSearchTerm('');
    setShowHistory(false);
  };

  // Ao pressionar Enter, adicionar ao histórico
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      addToHistory(searchTerm);
      setShowHistory(false);
    }
    if (e.key === 'Escape') {
      setShowHistory(false);
    }
  };

  return (
    <div className="mb-6 flex gap-4 items-center flex-wrap" data-testid="of-filters">
      {/* Caixa de Pesquisa Grande com Histórico */}
      <div className="relative flex-1 min-w-[300px] max-w-xl" ref={searchRef}>
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone-400" />
        <Input
          placeholder="Pesquisar por OF, artigo ou marca..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setShowHistory(true)}
          onKeyDown={handleKeyDown}
          className="pl-12 pr-12 h-12 text-base rounded-xl border-2 border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
          data-testid="of-search-input"
        />
        {searchTerm && (
          <button
            onClick={handleClearSearch}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors p-1 rounded-full hover:bg-stone-100"
            title="Limpar pesquisa"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        
        {/* Dropdown do Histórico */}
        {showHistory && searchHistory.length > 0 && (
          <div 
            ref={historyRef}
            className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border-2 border-stone-200 shadow-lg z-50 overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-2 bg-stone-50 border-b border-stone-100">
              <div className="flex items-center gap-2 text-sm text-stone-500">
                <Clock className="h-4 w-4" />
                <span>Pesquisas recentes</span>
              </div>
              <button
                onClick={clearHistory}
                className="text-xs text-stone-400 hover:text-red-500 flex items-center gap-1 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                Limpar tudo
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {searchHistory.map((term, idx) => (
                <div
                  key={idx}
                  onClick={() => selectHistoryTerm(term)}
                  className="flex items-center justify-between px-4 py-3 hover:bg-stone-50 cursor-pointer group transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Search className="h-4 w-4 text-stone-300" />
                    <span className="text-stone-700">{term}</span>
                  </div>
                  <button
                    onClick={(e) => removeFromHistory(term, e)}
                    className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-500 p-1 rounded transition-all"
                    title="Remover"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Filtro de Marca */}
      <select
        value={filterCliente}
        onChange={(e) => setFilterCliente(e.target.value)}
        className="px-4 py-3 border-2 border-stone-200 rounded-xl text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
        data-testid="of-filter-marca"
      >
        <option value="">Todas as marcas</option>
        {clientesUnicos.map((cliente) => (
          <option key={cliente} value={cliente}>{cliente}</option>
        ))}
      </select>
      
      {/* Filtro de Estado */}
      <select
        value={filterStatus}
        onChange={(e) => setFilterStatus(e.target.value)}
        className="px-4 py-3 border-2 border-stone-200 rounded-xl text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
        data-testid="of-filter-status"
      >
        <option value="">Todos os estados</option>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <option key={key} value={key}>{config.label}</option>
        ))}
      </select>
      
      {/* Ordenação */}
      <div className="flex items-center gap-2 ml-auto">
        <span className="text-sm text-stone-500">Ordenar:</span>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
          data-testid="of-sort-by"
        >
          <option value="data_criacao">Data Criação</option>
          <option value="data_entrega">Data Entrega</option>
          <option value="previsao_entrada">Previsão Entrada</option>
          <option value="cliente">Marca</option>
          <option value="numero_of">Nº OF</option>
          <option value="status">Estado</option>
          <option value="progresso">Progresso</option>
        </select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          className="px-2"
          data-testid="of-sort-order"
        >
          {sortOrder === 'asc' ? '↑' : '↓'}
        </Button>
      </div>
      
      {/* Limpar Filtros */}
      {(filterStatus || filterCliente) && (
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => { setFilterStatus(''); setFilterCliente(''); }}
          data-testid="of-clear-filters"
        >
          Limpar filtros
        </Button>
      )}
    </div>
  );
}
